import Select, { components, ControlProps, ValueContainerProps } from 'react-select'
import { Placeholder } from '../Placeholder'
import { useMemo } from 'react'

import styles from './styles.module.css'
import { SpDropDownInterface } from '@/types/components/molecules/SpDropdown'
import { useSpDropdown } from '@/hooks/components/SpDropdown'
import { Tag } from '../Tag'

const SpDropdown = ({
  onChange,
  onBlur,
  onKeyDown,
  value,
  isDisabled = false,
  isSearchable = false,
  options,
  optionsSelected,
  valueKeyName,
  labelKeyName,
  error = false,
  placeholder,
  name,
  isLoading = false,
  hideDropDownIndicator = false,
  isMulti = false,
  reference = null,
  className = '',
  minMenuHeight = 300,
  textAlign = 'left',
  minMenuWidth = '',
  zIndex = 10,
  id = '',
  StartAdornment = null,
  DropDownIndicatorIcon = null,
  useBodyAsAnchor = true,
  noOptionsMessage = 'No more options available',
  hideSelectedOptions = false,
  closeMenuOnSelect = true,
  onRemoveOption,
}: SpDropDownInterface): JSX.Element => {
  const { anchor, handleChange, handleOnBlur, handledValue, handledOptions,handleKeyDown } = useSpDropdown({
    isMulti,
    labelKeyName,
    onBlur,
    onChange,
    options,
    value,
    useBodyAsAnchor,
    valueKeyName,
    onKeyDown
  })

  const { DropdownIndicator } = components

  const CustomDropdownIndicator = useMemo(
    () => (props) => {
      if (DropDownIndicatorIcon)
        return (
          <span className={styles.customDropdownIndicator}>
            <DropDownIndicatorIcon />
          </span>
        )
      return <DropdownIndicator {...props} />
    },
    []
  )

  const Control = useMemo(
    () =>
      ({ children, ...props }: ControlProps) => {
        if (StartAdornment) {
          return (
            <components.Control {...props}>
              {StartAdornment} {children}
            </components.Control>
          )
        }

        return <components.Control {...props}>{children}</components.Control>
      },
    []
  )
  const ValueContainer = useMemo(
    () =>
      ({ children, ...props }: ValueContainerProps) => {
        if (isMulti) {
          return (
            <components.ValueContainer {...props}>
              <Placeholder externalClasses={styles.multiPlaceholder}>
                {isLoading ? 'Loading...' : placeholder}
              </Placeholder>
              {children}
            </components.ValueContainer>
          )
        }

        return <components.ValueContainer {...props}>{children}</components.ValueContainer>
      },
    [placeholder]
  )

  return (
    <>
      <Select
        ref={reference}
        className={className}
        classNamePrefix="spDropdown"
        id={id}
        name={name}
        options={handledOptions}
        onChange={handleChange}
        onBlur={handleOnBlur}
        onKeyDown={handleKeyDown}
        isMulti={isMulti}
        isClearable={false}
        hideSelectedOptions={hideSelectedOptions}
        noOptionsMessage={() => noOptionsMessage}
        value={handledValue}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        placeholder={isMulti ? '' : <Placeholder>{isLoading ? 'Loading...' : placeholder}</Placeholder>}
        blurInputOnSelect={!isMulti}
        closeMenuOnSelect={closeMenuOnSelect}
        isLoading={isLoading}
        menuPosition="fixed"
        menuPortalTarget={anchor}
        minMenuHeight={minMenuHeight}
        components={{ DropdownIndicator: CustomDropdownIndicator, Control, ValueContainer }}
        styles={{
          control: (baseStyles, state) => {
            return {
              ...baseStyles,
              border:
                error && !state.menuIsOpen && !state.isFocused
                  ? '1px solid red'
                  : state.isDisabled
                  ? '1px solid rgba(0,0,0,0.3)'
                  : state.menuIsOpen || state.isFocused
                  ? '2px solid rgba(0,0,0,0.7)'
                  : '1px solid rgba(0,0,0,0.3) , 12px solid red ',
              boxShadow:
                state.isFocused || state.menuIsOpen ? '2px solid rgba(0,0,0,0.7)' : '1px solid rgba(0,0,0,0.4)',
              padding: '3px 5px',
              minHeight: '45px',
              borderRadius: '5px',
              fontFamily: 'Inter',
              fontSize: '16px',
              lineHeight: '19px',
              fontWeight: 'normal',
              cursor: 'pointer',
              color: value ? 'rgb(170, 170, 170)' : '#000',
              transition: '0.1s ease-in-out',
              textAlign: textAlign,
              ':hover': {
                border:
                  error && !state.menuIsOpen && !state.isFocused
                    ? '1px solid red'
                    : state.isDisabled
                    ? '1px solid rgba(0,0,0,0.3)'
                    : state.menuIsOpen || state.isFocused
                    ? '2px solid rgba(0,0,0,0.7)'
                    : '1px solid rgba(0,0,0,0.3) , 12px solid red ',
                boxShadow:
                  state.isFocused || state.menuIsOpen ? '2px solid rgba(0,0,0,0.7)' : '1px solid rgba(0,0,0,0.4)',
                padding: '3px 5px',
                minHeight: '45px',
                borderRadius: '5px',
                fontFamily: 'Inter',
                fontSize: '16px',
                lineHeight: '19px',
                fontWeight: 'normal',
                cursor: 'pointer',
                color: value ? 'rgb(170, 170, 170)' : '#000',
                transition: '0.1s ease-in-out',
                textAlign: textAlign,
                ':hover': {
                  border:
                    state.menuIsOpen || state.isFocused ? '2px solid rgba(0,0,0,0.7)' : '1px solid rgba(0,0,0,1)',
                },
              },
            }
          },
          indicatorSeparator: (baseStyles) => ({
            ...baseStyles,
            display: 'none',
          }),
          dropdownIndicator: (baseStyles) => ({
            ...baseStyles,
            visibility: hideDropDownIndicator ? 'hidden' : 'visible',
            color: 'rgba(0, 0, 0, 0.54)',
          }),

          option: (baseStyles, { isFocused, isSelected, data }) => {
            return {
              ...baseStyles,
              background:
                isSelected || data.value == value
                  ? '#E6EDEC'
                  : !isSelected && isFocused
                  ? '#F5F5F5'
                  : baseStyles.background,
              color: '#000',
              padding: '10px 5px 10px 10px',
              fontSize: '1rem',
              lineHeight: '1.5rem',
              cursor: 'pointer',
              wordBreak: 'keep-all',
              ':active': {
                backgroundColor: '#E6EDEC',
              },
              ...data.classes,
            }
          },
          noOptionsMessage: (baseStyles) => ({
            ...baseStyles,

            color: 'rgb(170, 170, 170)',
            padding: '10px 5px 10px 10px',
            fontSize: '1rem',
            lineHeight: '1.5rem',
            textAlign: 'left',
          }),

          menu: (baseStyles) => ({
            ...baseStyles,
            zIndex: zIndex,
            minWidth: minMenuWidth,

            boxShadow:
              '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
          }),
          menuPortal: (baseStyles) => ({
            ...baseStyles,
            zIndex: zIndex,
          }),
          valueContainer: (baseStyles) => ({
            ...baseStyles,
            zIndex: zIndex,
            paddingRight: '0px',
            paddingLeft: '5px',
          }),
          indicatorsContainer: (baseStyles) => ({
            ...baseStyles,
            paddingLeft: '0px',
            div: {
              paddingLeft: '0px',
              paddingRight: '0px',
              color: 'rgba(0, 0, 0, 0.54)',
            },
          }),
          placeholder: (baseStyles) => ({
            ...baseStyles,
            fontSize: '15px',
            color: value ? 'rgb(170, 170, 170)' : '#000',
            span: {
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            },
            display: 'block !important',
          }),
          multiValueLabel: (baseStyles) => ({
            ...baseStyles,
            display: 'none',
          }),
          multiValueRemove: (baseStyles) => ({
            ...baseStyles,
            display: 'none',
          }),
          multiValue: (baseStyles) => ({
            ...baseStyles,
            display: 'none',
          }),
          clearIndicator: (baseStyles) => ({
            ...baseStyles,
            display: 'none',
          }),
        }}
      />
      {Boolean(isMulti && optionsSelected?.length > 0) &&
        optionsSelected.map(({ id, name }) => {
          return <Tag key={id} id={id} name={name} isDisabled={isDisabled} onClick={() => onRemoveOption(id)} />
        })}
    </>
  )
}

export default SpDropdown
